/**
 * Desktop mega-menu content alignment (Neal Avionics).
 *
 * The mega-menu panel is Horizon's native full-width submenu and MUST stay that
 * way — the aviation-blueprint layer depends on it spanning the header. What
 * this module does is align the *link block inside* that panel underneath the
 * top-level item that opened it.
 *
 * Why it is needed: the links live in `.mega-menu__column`, which is column 1 of
 * the six-column `.mega-menu__grid`. Column 1 is always flush with the left edge
 * of the page-width content column, so without this every submenu's links would
 * start at the far left regardless of which trigger was hovered.
 *
 * What it publishes: a single `--submenu-content-left` custom property on each
 * `.menu-list__submenu`. CSS consumes it as `inset-inline-start` on a
 * relatively-positioned `.mega-menu__list` — a paint-only offset, so the grid
 * tracks keep their natural widths and neither the panel nor the blueprint move.
 *
 * Notes:
 * - Offsets are computed eagerly on load/resize so the value is already correct
 *   before a submenu is ever revealed. That is what prevents a visible jump.
 * - The measurement is idempotent: it is derived from the trigger and the grid,
 *   never from the already-shifted column, so re-running it never drifts.
 * - Desktop only. The mobile drawer is a different DOM and is never touched.
 * - `[slot='overflow']` items are excluded, matching the blueprint's own scope.
 * - Read-only with respect to focus/hover/aria — it only writes a CSS variable,
 *   so hover, keyboard navigation, focus order and Escape-to-close are intact.
 */

const DESKTOP_MIN_WIDTH = 750;
const ITEM_SELECTOR = '.menu-list__list-item:not([slot="overflow"])';
const ALIGNED_SUBMENU = '.menu-list__submenu--trigger-aligned';

/** Optical nudge, in px. The brief allows 0–8px; 0 aligns the submenu link text
 *  exactly with the top-level label text. */
const OPTICAL_ADJUST = 0;

/**
 * Align one top-level item's link block under its trigger.
 * @param {Element | null} item
 */
function alignSubmenu(item) {
  if (!item) return;

  const submenu = item.querySelector(':scope > .menu-list__submenu');
  if (!(submenu instanceof HTMLElement) || !submenu.matches(ALIGNED_SUBMENU)) return;

  if (window.innerWidth < DESKTOP_MIN_WIDTH) {
    submenu.style.removeProperty('--submenu-content-left');
    return;
  }

  const grid = submenu.querySelector('.mega-menu__grid');
  const columns = submenu.querySelectorAll('.mega-menu__column');
  // The label span, not the <a>: the anchor carries horizontal padding, so the
  // span's content edge is where the visible text actually starts.
  const label = item.querySelector(':scope > .menu-list__link .menu-list__link-title');

  if (!(grid instanceof HTMLElement) || !(label instanceof HTMLElement) || columns.length === 0) return;

  const gridRect = grid.getBoundingClientRect();
  const labelRect = label.getBoundingClientRect();

  // Column 1's natural (unshifted) left edge is the grid's content left edge,
  // so the offset is measured against gridRect — never against the column,
  // which may already be carrying a previous shift.
  const labelPadding = parseFloat(getComputedStyle(label).paddingInlineStart) || 0;
  const naturalLeft = gridRect.left;
  const targetLeft = labelRect.left + labelPadding + OPTICAL_ADJUST;

  // Right-edge clamp: the link block may never be pushed past the usable width.
  // Measured across all columns, so multi-column submenus clamp correctly too.
  const firstRect = columns[0].getBoundingClientRect();
  const lastRect = columns[columns.length - 1].getBoundingClientRect();
  const blockWidth = Math.max(lastRect.right - firstRect.left, 0);
  const maxOffset = Math.max(gridRect.width - blockWidth, 0);

  const offset = Math.min(Math.max(targetLeft - naturalLeft, 0), maxOffset);

  submenu.style.setProperty('--submenu-content-left', `${Math.round(offset)}px`);
}

/** Align every top-level submenu. Cheap enough to run whole-menu on layout change. */
function alignAll() {
  document.querySelectorAll(ITEM_SELECTOR).forEach(alignSubmenu);
}

/** Track the last hovered item so we only re-measure when it actually changes. */
let hoveredItem = null;

/** @param {Event} event */
function onPointerOver(event) {
  const target = event.target;
  const item = target instanceof Element ? target.closest(ITEM_SELECTOR) : null;
  if (item === hoveredItem) return;
  hoveredItem = item;
  alignSubmenu(item);
}

/** @param {Event} event */
function onFocusIn(event) {
  const target = event.target;
  const item = target instanceof Element ? target.closest(ITEM_SELECTOR) : null;
  alignSubmenu(item);
}

let resizeTimer;
function onResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(alignAll, 100);
}

// `#header-group` is a stable wrapper in theme.liquid that persists across
// section hydration, so delegated listeners bound here survive header re-renders.
const container = document.getElementById('header-group') || document;
container.addEventListener('pointerover', onPointerOver);
container.addEventListener('focusin', onFocusIn);
window.addEventListener('resize', onResize);

// Eager pass: get every offset right before anything can be opened, so the first
// hover never shows the links snapping from the far left into place. A second
// pass after webfonts land catches any label reflow.
alignAll();
requestAnimationFrame(alignAll);
if (document.fonts?.ready) document.fonts.ready.then(alignAll);
